/**
 * Securely retrieves the user's IP address captured during SSR
 * IP is extracted from request headers on the server and passed to client via window object
 *
 * Security considerations:
 * - IP is captured server-side from request headers
 * - No additional API calls needed
 * - Handles proxy headers (X-Forwarded-For, X-Real-IP)
 * - IP should be stored with encryption and proper data retention policies
 */

/**
 * Gets user IP address from window object (set during SSR)
 * Returns null if IP is not available
 */
const getUserIpAddress = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const ip = (window as any).__USER_IP_ADDRESS__;
    return ip || null;
  } catch (error) {
    console.warn(
      "Failed to retrieve user IP address:",
      error instanceof Error ? error.message : "Unknown error",
    );
    return null;
  }
};

export const extractIpFromRequest = (req: any): string | null => {
  try {
    const forwardedFor = req.headers["x-forwarded-for"];
    if (forwardedFor) {
      const ips = forwardedFor.split(",").map((ip: string) => ip.trim());
      return ips[0] || null;
    }

    const realIp = req.headers["x-real-ip"];
    if (realIp) {
      return realIp;
    }

    const cfIp = req.headers["cf-connecting-ip"];
    if (cfIp) {
      return cfIp;
    }

    const clientIp = req.headers["x-client-ip"];
    if (clientIp) {
      return clientIp;
    }

    const directIp = req.connection?.remoteAddress || req.socket?.remoteAddress;
    if (directIp) {
      return directIp.replace(/^::ffff:/, "");
    }

    return null;
  } catch (error) {
    console.error("Error extracting IP from request:", error);
    return null;
  }
};

export default getUserIpAddress;
